import {
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  ModalOptions,
  ModalRef,
  ModalService,
} from '../../services/modal.service';
import { ModalAgregarTelefonoComponent } from './modal-agregar-telefono/modal-agregar-telefono.component';
import { ModalEditarTelefonoComponent } from './modal-editar-telefono/modal-editar-telefono.component';
import { ModalEliminarTelefonoComponent } from './modal-eliminar-telefono/modal-eliminar-telefono.component';
import { Sucursal } from '../../models/sucursal.model';
import { Telefono } from '../../models/telefono.model';
import { ModalActualizarCreditoWhatsappComponent } from './modal-actualizar-credito-whatsapp/modal-actualizar-credito-whatsapp.component';
import {
  CreditoWhatsapp,
  HistorialCreditoWhatsapp,
  HistorialCreditoWhatsappFiltros,
} from '../../models/credito-whatsapp.model';
import { CreditoWhatsappService } from '../../services/creditoWhatsapp.service';
import { ToastService } from '../../services/toast.service';

interface ModalInfo {
  telefonoEditing?: Telefono;
  historialLog?: HistorialCreditoWhatsapp;
  template: TemplateRef<unknown>;
  options?: ModalOptions;
}

@Component({
  selector: 'app-telefonos',
  imports: [
    DatePipe,
    ModalAgregarTelefonoComponent,
    ModalEditarTelefonoComponent,
    ModalEliminarTelefonoComponent,
    ModalActualizarCreditoWhatsappComponent,
  ],
  templateUrl: './telefonos.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './telefonos.component.css',
})
export class TelefonosComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly creditoWhatsappService = inject(CreditoWhatsappService);
  private readonly toastService = inject(ToastService);

  sucursal = input.required<Sucursal>();
  telefonoEditing = signal<Telefono | null>(null);
  historialLogSeleccionado = signal<HistorialCreditoWhatsapp | null>(null);
  historialCreditos = signal<HistorialCreditoWhatsapp[]>([]);
  loadingHistorial = signal(false);
  fechaDesde = signal('');
  fechaHasta = signal('');
  telefonoFiltroId = signal<number | null>(null);
  cerrar = output<void>();

  modalStack: Map<TemplateRef<unknown>, ModalRef> = new Map();

  ngOnInit(): void {
    this.cargarHistorialCreditos();
  }

  abrirModal(modalInfo: ModalInfo): void {
    if (modalInfo.telefonoEditing) {
      this.telefonoEditing.set(modalInfo.telefonoEditing);
    } else if (this.telefonoEditing() != null) {
      this.telefonoEditing.set(null);
    }

    if (modalInfo.historialLog) {
      this.historialLogSeleccionado.set(modalInfo.historialLog);
    } else if (this.historialLogSeleccionado() != null) {
      this.historialLogSeleccionado.set(null);
    }

    const modalRef = this.modalService.open(
      modalInfo.template,
      modalInfo.options,
    );
    this.modalStack.set(modalInfo.template, modalRef);
  }
  cerrarModal(modalTemplate: TemplateRef<unknown>): void {
    const modalRef = this.modalStack.get(modalTemplate);
    if (modalRef) {
      modalRef.close();
      this.modalStack.delete(modalTemplate);
    }
  }

  onTelefonoAgregado(telefono: Telefono): void {
    this.sucursal().telefonos.push(telefono);
  }
  onTelefonoEditado(telefonoEditado: Telefono): void {
    const telefonos = this.sucursal().telefonos;
    const index = telefonos.findIndex((t) => t.id === telefonoEditado.id);
    if (index !== -1) {
      telefonos[index] = telefonoEditado;
    }
  }
  onTelefonoEliminado(telefonoIdEliminado: number): void {
    const telefonos = this.sucursal().telefonos;
    const index = telefonos.findIndex((t) => t.id === telefonoIdEliminado);
    if (index !== -1) {
      telefonos.splice(index, 1);
    }
  }
  onCreditoActualizado(creditoActualizado: CreditoWhatsapp): void {
    const telefonos = this.sucursal().telefonos;
    const telefono = telefonos.find(
      (t) => t.id === creditoActualizado.telefono_id,
    );
    if (telefono) {
      telefono.creditoWhatsapp = creditoActualizado;
    }

    this.cargarHistorialCreditos();
  }

  onFechaDesdeChange(value: string): void {
    this.fechaDesde.set(value);
  }

  onFechaHastaChange(value: string): void {
    this.fechaHasta.set(value);
  }

  onTelefonoFiltroChange(value: string): void {
    this.telefonoFiltroId.set(value ? Number(value) : null);
  }

  verMotivo(
    log: HistorialCreditoWhatsapp,
    template: TemplateRef<unknown>,
  ): void {
    this.abrirModal({
      template,
      historialLog: log,
      options: {
        size: 'sm',
        centered: true,
      },
    });
  }

  aplicarFiltrosHistorial(): void {
    if (
      this.fechaDesde() &&
      this.fechaHasta() &&
      new Date(this.fechaDesde()) > new Date(this.fechaHasta())
    ) {
      this.toastService.show({
        text: 'La fecha desde no puede ser mayor que la fecha hasta.',
        classname: 'bg-info text-dark',
        delay: 3000,
      });
      return;
    }

    this.cargarHistorialCreditos();
  }

  limpiarFiltrosHistorial(): void {
    this.fechaDesde.set('');
    this.fechaHasta.set('');
    this.telefonoFiltroId.set(null);
    this.cargarHistorialCreditos();
  }

  private buildFiltrosHistorial(): HistorialCreditoWhatsappFiltros {
    return {
      empresa_id: this.sucursal().empresa_id,
      sucursal_id: this.sucursal().id,
      telefono_id: this.telefonoFiltroId() ?? undefined,
      fecha_desde: this.fechaDesde() || undefined,
      fecha_hasta: this.fechaHasta() || undefined,
    };
  }

  private cargarHistorialCreditos(): void {
    this.loadingHistorial.set(true);
    this.creditoWhatsappService
      .obtenerHistorial(this.buildFiltrosHistorial())
      .subscribe({
        next: (logs) => {
          this.historialCreditos.set(logs);
          this.loadingHistorial.set(false);
        },
        error: (error) => {
          this.loadingHistorial.set(false);
          this.toastService.show({
            text:
              error?.message || 'No se pudo cargar el historial de créditos.',
            classname: 'bg-red-500 text-white',
            delay: 4500,
          });
        },
      });
  }
}
